import {
  DOCUMENT_MODEL_RENDER_FORMATS,
  type DocumentModelDTO,
  type DocumentModelKindDTO,
  type DocumentThemePresetDTO,
} from '@shared/schemas/document-model';

export type ExportFormat = 'docx' | 'pdf' | 'pptx' | 'xlsx';

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  docx: 'Word',
  pdf: 'PDF',
  pptx: 'PowerPoint',
  xlsx: 'Excel',
};

/** Formats téléchargeables d'une famille de modèle (le HTML est l'aperçu, pas un export). */
export function getExportFormats(kind: DocumentModelKindDTO): ExportFormat[] {
  return DOCUMENT_MODEL_RENDER_FORMATS[kind].filter((format): format is ExportFormat => format !== 'html');
}

/** Vocabulaire de l'unité éditable : section (rapport), diapositive (présentation), feuille (classeur). */
export function getSectionNouns(kind: DocumentModelKindDTO) {
  if (kind === 'deck') {
    return { article: 'la', plural: 'diapositives', singular: 'diapositive', thisOne: 'cette diapositive' } as const;
  }
  if (kind === 'workbook') {
    return { article: 'la', plural: 'feuilles', singular: 'feuille', thisOne: 'cette feuille' } as const;
  }
  return { article: 'la', plural: 'sections', singular: 'section', thisOne: 'cette section' } as const;
}

export const THEME_OPTIONS: ReadonlyArray<{
  accent: string;
  label: string;
  preset: DocumentThemePresetDTO;
  primary: string;
}> = [
  { accent: '#2F6FDE', label: 'Corporate', preset: 'corporate', primary: '#1B2A41' },
  { accent: '#C8A24A', label: 'Moderne', preset: 'modern', primary: '#1F2937' },
  { accent: '#A4743A', label: 'Exécutif', preset: 'executive', primary: '#111111' },
  { accent: '#5B84A6', label: 'Institutionnel', preset: 'institutional', primary: '#2F4A66' },
  { accent: '#9A9A9A', label: 'Minimal', preset: 'minimal', primary: '#222222' },
  { accent: '#B5523B', label: 'Éditorial', preset: 'editorial', primary: '#3B2F2F' },
];

export function getThemeLabel(preset: DocumentThemePresetDTO) {
  return THEME_OPTIONS.find((option) => option.preset === preset)?.label ?? preset;
}

export type EditableUnit = {
  id: string;
  title: string;
};

/** Sections (rapport), diapositives (présentation) ou feuilles (classeur) d'un modèle, dans l'ordre. */
export function listEditableUnits(model: DocumentModelDTO): EditableUnit[] {
  if (model.kind === 'report') {
    return model.sections.map((section) => ({ id: section.id, title: section.title }));
  }
  if (model.kind === 'deck') {
    return model.slides.map((slide) => ({ id: slide.id, title: slide.title }));
  }
  return model.sheets.map((sheet) => ({ id: sheet.id, title: sheet.name }));
}

/** Bloc d'une section/slide du modèle (le classeur n'a pas de blocs éditables). */
export function findBlock(model: DocumentModelDTO | null | undefined, sectionId: string, blockId: string) {
  if (!model || model.kind === 'workbook') {
    return null;
  }
  const blocks = model.kind === 'report'
    ? model.sections.find((section) => section.id === sectionId)?.blocks
    : model.slides.find((slide) => slide.id === sectionId)?.blocks;
  return blocks?.find((block) => block.id === blockId) ?? null;
}
